import React, { useMemo, useState } from "react";
import { CalendarClock, MapPin, X } from "lucide-react";

import type { PlaceRecord } from "lib/api/places";
import { DateTimePickerInput } from "ui/DateTimePicker";
import PlacePicker from "components/Places/PlacePicker";

interface TimelinePlaceInputProps {
  timelineValue: Date | null;
  placeValue: PlaceRecord | null;
  onTimelineChange: (value: Date | null) => void;
  onPlaceChange: (value: PlaceRecord | null) => void;
  isMobile?: boolean;
}

const formatDateLabel = (value: Date | null): string =>
  value ? value.toLocaleString() : "";

/**
 * Compact chip-bar input for optional timeline and place metadata.
 * Designed to sit alongside comment privacy controls without overwhelming the form.
 */
const TimelinePlaceInput: React.FC<TimelinePlaceInputProps> = ({
  timelineValue,
  placeValue,
  onTimelineChange,
  onPlaceChange,
  isMobile = false,
}) => {
  const [showTimelinePicker, setShowTimelinePicker] = useState(false);
  const [showPlacePicker, setShowPlacePicker] = useState(false);

  const placeLabel = useMemo(() => {
    if (!placeValue) {
      return "";
    }
    return placeValue.formatted_address || placeValue.name;
  }, [placeValue]);

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setShowTimelinePicker((previous) => !previous)}
          className="rounded-full bg-eoe-icon px-3 py-1.5 text-xs font-semibold text-eoe-text transition-colors hover:bg-eoe-icon-hover dark:bg-eoe-dark-icon dark:text-eoe-dark-text dark:hover:bg-eoe-dark-icon-hover"
        >
          <span className="inline-flex items-center gap-1">
            <CalendarClock className="size-3.5" aria-hidden />
            {timelineValue ? "Timeline set" : "+ Timeline"}
          </span>
        </button>
        {timelineValue ? (
          <button
            type="button"
            onClick={() => onTimelineChange(null)}
            className="rounded-full border border-eoe-border-main px-2 py-1 text-xs text-eoe-text-sub hover:text-eoe-text dark:border-eoe-dark-border-main dark:text-eoe-dark-text-sub dark:hover:text-eoe-dark-text"
            aria-label="Clear timeline"
          >
            <X className="size-3.5" aria-hidden />
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => setShowPlacePicker((previous) => !previous)}
          className="rounded-full bg-eoe-icon px-3 py-1.5 text-xs font-semibold text-eoe-text transition-colors hover:bg-eoe-icon-hover dark:bg-eoe-dark-icon dark:text-eoe-dark-text dark:hover:bg-eoe-dark-icon-hover"
        >
          <span className="inline-flex items-center gap-1">
            <MapPin className="size-3.5" aria-hidden />
            {placeValue ? "Place set" : "+ Place"}
          </span>
        </button>
        {placeValue ? (
          <button
            type="button"
            onClick={() => onPlaceChange(null)}
            className="rounded-full border border-eoe-border-main px-2 py-1 text-xs text-eoe-text-sub hover:text-eoe-text dark:border-eoe-dark-border-main dark:text-eoe-dark-text-sub dark:hover:text-eoe-dark-text"
            aria-label="Clear place"
          >
            <X className="size-3.5" aria-hidden />
          </button>
        ) : null}
      </div>

      {timelineValue ? (
        <div className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
          {formatDateLabel(timelineValue)}
        </div>
      ) : null}
      {placeLabel ? (
        <div className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
          {placeLabel}
        </div>
      ) : null}

      {showTimelinePicker ? (
        <DateTimePickerInput
          value={timelineValue}
          onChange={onTimelineChange}
          isMobile={isMobile}
          placeholder="Choose a timeline time"
        />
      ) : null}

      {showPlacePicker ? (
        <PlacePicker value={placeValue} onChange={onPlaceChange} />
      ) : null}
    </div>
  );
};

export default TimelinePlaceInput;
